import { v4 as uuid } from 'uuid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { administrationApi } from '@/services/instanceAdministration'
import { configurationEditingApi, type ConfigurationApplyCommand, type ConfigurationApplyResult } from '@/services/instanceConfigurationEditing'
import { ApiError } from '@/services/http/errors'
import { ADMIN_DATA } from './useAccess'
import { useRoles } from './useRoles'
import { acceptedDraft, contentDraft, reloadDraft, draftOf, filesOf, payloadOf, hasChanges, createFile as addDraftFile,
  deleteFile as removeDraftFile, editCatalog as catalogDraft, type Draft, type CatalogEdit, type ApplyIssue } from '../config/draft'
import { definitionView, editDefinitionIcon, type DefinitionIconEdit } from '../config/definitions'
import type { EditResult } from '../contracts'

type Pending = { instanceId: string; controller: AbortController; promise: Promise<ConfigurationApplyResult> }
const statusOf = (error: unknown) => (error as ApiError | null)?.status
const issueOf = (error: unknown): ApplyIssue => {
  const status = statusOf(error)
  const kind = status === 409 ? 'conflict' : (!status || status >= 500 ? 'unknown' : 'failure')
  return { kind, message: (error as Error | null)?.message ?? '配置应用失败' }
}
/** Own edit drafts separately from server snapshots; a late response may only update its own instance. */
export const useConfiguration = (userId: string, onAccessFailure: () => void) => {
  const client = useQueryClient()
  const [requestedInstanceId, setRequestedInstanceId] = useState<string | null>(null)
  const [drafts, setDrafts] = useState(new Map<string, Draft>())
  const [savingInstances, setSavingInstances] = useState(new Set<string>())
  const latestDrafts = useRef(drafts)
  // Draft and rendered projection advance together; batched commands read the latest accepted edit.
  const commitDrafts = useCallback((update: (previous: Map<string, Draft>) => Map<string, Draft>) => {
    const next = update(latestDrafts.current)
    latestDrafts.current = next
    setDrafts(next)
  }, [])
  const pending = useRef(new Map<string, Pending>())
  const operationIds = useRef(new Map<string, string>())
  const live = useRef(true)
  const directory = useQuery({ queryKey: [...ADMIN_DATA, userId, 'instances'],
    queryFn: ({ signal }) => administrationApi.instances(signal), retry: false })
  const roleCreator = useRoles(userId, directory.data?.catalog_revision ?? 0, onAccessFailure)
  const instanceId = requestedInstanceId ?? directory.data?.default_instance_id ?? ''
  const key = useMemo(() => [...ADMIN_DATA, userId, 'configuration', instanceId], [instanceId, userId])
  const current = useQuery({ queryKey: key, enabled: !!instanceId,
    queryFn: ({ signal }) => configurationEditingApi.read(instanceId, signal), retry: false })
  const { refetch: refetchCurrent } = current
  const { refetch: refetchDirectory } = directory
  const updateDraft = useCallback((id: string, update: (draft: Draft) => Draft, resultPath?: string): EditResult => {
    const existing = latestDrafts.current.get(id)
    if (!existing) return { kind: 'rejected', message: '配置尚未读取' }
    try {
      const next = update(existing)
      commitDrafts((previous) => new Map(previous).set(id, next))
      return { kind: 'accepted' }
    } catch (error) {
      const message = (error as Error).message
      commitDrafts((previous) => new Map(previous).set(id, { ...existing,
        issue: { kind: 'failure', message }, outcome: null, resultPath: resultPath ?? existing.path }))
      return { kind: 'rejected', message }
    }
  }, [commitDrafts])
  useEffect(() => {
    const snapshot = current.data
    if (!snapshot) return
    commitDrafts((previous) => previous.has(snapshot.instance_id) ? previous : new Map(previous).set(snapshot.instance_id, draftOf(snapshot)))
  }, [commitDrafts, current.data])
  useEffect(() => {
    if ([directory.error, current.error].some((error) => [401, 403].includes(statusOf(error) ?? 0))) onAccessFailure()
  }, [current.error, directory.error, onAccessFailure])
  useEffect(() => {
    live.current = true
    const requests = pending.current
    return () => {
      live.current = false
      requests.forEach(({ controller }) => controller.abort())
      requests.clear()
    }
  }, [])

  const draft = drafts.get(instanceId)
  const files = useMemo(() => draft ? filesOf(draft) : [], [draft])
  const file = files.find((item) => item.path === draft?.path)
  const definitionContent = files.find((item) => item.path === 'workflow.yaml')?.content
  const definitions = useMemo(() => {
    try { return { entries: definitionView(definitionContent ?? ''), error: null } }
    catch (error) { return { entries: [], error: (error as Error).message } }
  }, [definitionContent])
  const selectInstance = useCallback((id: string) => {
    if (directory.data?.instances.some((item) => item.instance_id === id && item.enabled)) setRequestedInstanceId(id)
  }, [directory.data])
  const selectFile = useCallback((path: string) => {
    updateDraft(instanceId, (value) => filesOf(value).some((item) => item.path === path) ? { ...value, path } : value)
  }, [instanceId, updateDraft])
  const change = useCallback((content: string) => {
    updateDraft(instanceId, (value) => ({ ...value, values: new Map(value.values).set(value.path, content), issue: null, outcome: null }))
  }, [instanceId, updateDraft])
  const editIcon = useCallback((command: DefinitionIconEdit) => updateDraft(instanceId, (value) => {
    const path = 'workflow.yaml'
    const content = filesOf(value).find((item) => item.path === path)?.content
    if (content === undefined) throw new Error('配置文件已不存在')
    return { ...value, values: new Map(value.values).set(path, editDefinitionIcon(content, command)), issue: null, outcome: null }
  }, 'workflow.yaml'), [instanceId, updateDraft])
  const reload = useCallback(async (discardChanges = false) => {
    const selectedPath = latestDrafts.current.get(instanceId)?.path ?? ''
    await refetchDirectory()
    if (!instanceId) return
    const accepted = await refetchCurrent()
    if (!live.current || !accepted.data || accepted.error) return
    const snapshot = accepted.data
    commitDrafts((previous) => {
      const existing = previous.get(snapshot.instance_id)
      if (!discardChanges && existing && hasChanges(existing)) return previous
      const restored = existing ? reloadDraft(existing, snapshot, selectedPath) : draftOf(snapshot)
      return new Map(previous).set(snapshot.instance_id, restored)
    })
  }, [commitDrafts, instanceId, refetchCurrent, refetchDirectory])
  const submit = useCallback((submitted: Draft, owner: 'catalog' | 'content', selectPath?: string): Promise<ConfigurationApplyResult> => {
    const selectedPath = latestDrafts.current.get(instanceId)?.path
    const payload = payloadOf(submitted)
    const identity = JSON.stringify([instanceId, payload])
    const existing = pending.current.get(identity)
    if (existing) return existing.promise
    if ([...pending.current.values()].some((request) => request.instanceId === instanceId)) {
      return Promise.reject(new Error('该智能体角色正在应用配置'))
    }
    const requestId = operationIds.current.get(identity) ?? uuid()
    operationIds.current.set(identity, requestId)
    const command: ConfigurationApplyCommand = { ...payload, request_id: requestId }
    const controller = new AbortController()
    setSavingInstances((previous) => new Set(previous).add(instanceId))
    const promise = configurationEditingApi.apply(instanceId, command, controller.signal).then((accepted) => {
      controller.signal.throwIfAborted()
      if (!live.current) throw new DOMException('Aborted', 'AbortError')
      if (accepted.configuration.tenant_id !== submitted.base.tenant_id) throw new TypeError('配置响应范围不一致')
      if (accepted.status !== 'active') throw new ApiError('配置已有后续修改，请重新读取后确认', { status: 409 })
      client.setQueryData([...ADMIN_DATA, userId, 'configuration', instanceId], accepted.configuration)
      updateDraft(instanceId, (value) => {
        const next = acceptedDraft(value, accepted)
        const path = selectPath && value.path === selectedPath ? selectPath : next.path
        return owner === 'catalog' ? { ...next, path, issue: value.issue, outcome: value.outcome, resultPath: value.resultPath }
          : { ...next, resultPath: submitted.path }
      })
      return accepted
    }).catch((error: unknown) => {
      if (owner === 'content' && live.current && !controller.signal.aborted) updateDraft(instanceId, (value) => ({ ...value, issue: issueOf(error), outcome: null, resultPath: submitted.path }))
      if (live.current && [401, 403].includes(statusOf(error) ?? 0)) onAccessFailure()
      throw error
    }).finally(() => {
      pending.current.delete(identity)
      if (live.current) setSavingInstances((previous) => new Set([...previous].filter((id) => id !== instanceId)))
    })
    pending.current.set(identity, { instanceId, controller, promise })
    return promise
  }, [client, instanceId, onAccessFailure, updateDraft, userId])
  const currentDraft = useCallback(() => {
    const value = latestDrafts.current.get(instanceId)
    if (!value) throw new Error('配置尚未读取')
    return value
  }, [instanceId])
  const save = useCallback(() => submit(contentDraft(currentDraft()), 'content'), [currentDraft, submit])
  const saveDefinitions = useCallback(() => submit(contentDraft(currentDraft(), 'workflow.yaml'), 'content'), [currentDraft, submit])
  const createFile = useCallback((name: string, content: string, label: string, category: string) => {
    const command = addDraftFile(draftOf(currentDraft().base), name, content, label, category)
    return submit(command, 'catalog', command.path)
  }, [currentDraft, submit])
  const deleteFile = useCallback((path: string) => {
    const value = currentDraft()
    if (hasChanges(contentDraft(value, path))) throw new Error('此文件有未保存内容，请先保存或放弃修改')
    return submit(removeDraftFile(draftOf(value.base), path), 'catalog')
  }, [currentDraft, submit])
  const editCatalog = useCallback((command: CatalogEdit) =>
    submit(catalogDraft(draftOf(currentDraft().base), command), 'catalog'), [currentDraft, submit])
  const ownsResult = draft?.resultPath === draft?.path

  return { userId, directory, instanceId, selectInstance, selectFile, change, reload, save, roleCreator,
    files, catalog: draft?.metadata, createFile, deleteFile, editCatalog, editIcon,
    definitions, saveDefinitions,
    definitionChanged: draft ? hasChanges(contentDraft(draft, 'workflow.yaml')) : false,
    definitionIssue: draft?.resultPath === 'workflow.yaml' ? draft.issue : null,
    snapshot: draft?.base, file, content: file ? draft?.values.get(file.path) ?? file.content : '',
    changed: draft ? hasChanges(contentDraft(draft)) : false, saving: savingInstances.has(instanceId),
    issue: ownsResult || draft?.issue?.kind === 'conflict' ? draft?.issue ?? null : null,
    outcome: ownsResult ? draft?.outcome ?? null : null,
    readError: directory.error ?? current.error, loading: directory.isPending || (!!instanceId && current.isPending) }
}
